/**
 * Whether charts show the reading that follows the pointer, and where it sits.
 *
 * One setting for every chart this person looks at - Explore, grid panels,
 * SFOC, dashboard tiles, heatmap, correlation, pie and map - so it belongs to
 * the person, not to any one chart. That is why it is not a `PlotStyle` key: a
 * style key would travel with links and saved charts, and opening somebody's
 * dashboard should not switch your own tooltips back on (or off).
 *
 * Every renderer writes its readout into a `.uplot-tooltip` node (or a Leaflet
 * tooltip on the map), so the switch is one class on the document root and one
 * CSS rule, rather than a check threaded through a dozen draw paths that the
 * next plot type would forget. Only the readout is hidden: the crosshair,
 * focus on hover, the legend, keyboard access and clicks keep working.
 *
 * The position is read by `placeTooltip` in `render/tooltip.ts`, the one
 * placement every uPlot renderer shares. The map keeps Leaflet's own tooltip.
 */

import { preferences, rememberPreference } from './preferences.ts';

/** The preference key, stored with the rest of the chart appearance. */
export const HOVER_TOOLTIP_KEY = 'show_tooltip';

/** The class on `<html>` that hides every hover readout. */
export const HOVER_TOOLTIPS_OFF_CLASS = 'hover-tooltips-off';

/** A corner of the plot (fixed), or a side of the pointer (follow). */
export type TooltipCorner = 'tl' | 'tr' | 'bl' | 'br';
export type TooltipMode = 'fixed' | 'follow';

export interface TooltipPosition {
  mode: TooltipMode;
  /** Where a fixed tooltip is pinned. */
  corner: TooltipCorner;
  /** Which side of the pointer a following tooltip keeps to. */
  side: TooltipCorner;
  /** A fixed tooltip goes see-through while the pointer is under it. */
  fade: boolean;
}

const CORNERS: readonly string[] = ['tl', 'tr', 'bl', 'br'];
const corner = (v: unknown, fallback: TooltipCorner): TooltipCorner =>
  CORNERS.includes(v as string) ? (v as TooltipCorner) : fallback;

/** On unless this person has chosen off; anything malformed reads as on. */
export function hoverTooltipsOn(chart: Record<string, unknown> = preferences.value.chart): boolean {
  return chart[HOVER_TOOLTIP_KEY] !== false;
}

/** Following the pointer, below right of it, unless chosen otherwise; malformed reads as the default. */
export function tooltipPosition(chart: Record<string, unknown> = preferences.value.chart): TooltipPosition {
  return {
    mode: chart.tooltip_mode === 'fixed' ? 'fixed' : 'follow',
    corner: corner(chart.tooltip_corner, 'br'),
    side: corner(chart.tooltip_side, 'br'),
    fade: chart.tooltip_fade !== false,
  };
}

/** Record the choice: applied everywhere now, remembered on the account shortly. */
export function setHoverTooltips(on: boolean): void {
  rememberPreference({ chart: { [HOVER_TOOLTIP_KEY]: on } });
}

/** Change where the tooltip sits; only the keys given are written. */
export function setTooltipPosition(patch: Partial<TooltipPosition>): void {
  const chart: Record<string, unknown> = {};
  if (patch.mode) chart.tooltip_mode = patch.mode;
  if (patch.corner) chart.tooltip_corner = patch.corner;
  if (patch.side) chart.tooltip_side = patch.side;
  if (patch.fade !== undefined) chart.tooltip_fade = patch.fade;
  rememberPreference({ chart });
}

function apply(): void {
  if (typeof document === 'undefined') return;
  document.documentElement.classList.toggle(HOVER_TOOLTIPS_OFF_CLASS, !hoverTooltipsOn());
}

apply();
preferences.subscribe(apply);
